import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Eye, EyeOff, Instagram } from 'lucide-react'
import { auth } from '@/lib/firebase'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { handleUserData } from '@/lib/firebase'
import { useAnalytics } from '@/hooks/useAnalytics'
import { logAuthAttempt } from '@/lib/securityLogging'
import { trackTikTokSignUp } from '@/lib/tiktokPixel'
import { trackSignUp as trackMetaPixelSignUp } from '@/lib/metaPixel'
import { getDeviceInfo, logDeviceInfoDebug } from '@/lib/deviceInfo'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { SignupFieldInfoDialog } from '@/components/auth/SignupFieldInfoDialog'

interface SignupSlideProps {
  onSuccess: () => void
  onLogin: () => void
  onFormValidChange: (isValid: boolean) => void
  onReferralPromptChange: (showing: boolean) => void
}

export function SignupSlide({ onSuccess, onLogin, onFormValidChange, onReferralPromptChange }: SignupSlideProps) {
  const { t } = useTranslation()
  const { trackSignUp } = useAnalytics()
  
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showInstagramInfo, setShowInstagramInfo] = useState(false)
  const [showReferralPrompt, setShowReferralPrompt] = useState(false)
  const [newUserId, setNewUserId] = useState<string | null>(null)
  const [referralSource, setReferralSource] = useState('')
  const [referralSourceOther, setReferralSourceOther] = useState('')
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    instagramHandle: ''
  })

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError(null)
  }

  // Update parent when form validity changes
  useEffect(() => {
    if (showReferralPrompt) {
      onFormValidChange(!!referralSource)
    } else {
      const isValid = !!(
        formData.email.trim() &&
        formData.password.trim() &&
        formData.instagramHandle.trim()
      )
      onFormValidChange(isValid)
    }
  }, [formData.email, formData.password, formData.instagramHandle, referralSource, showReferralPrompt, onFormValidChange])

  // Notify parent when referral prompt state changes
  useEffect(() => {
    onReferralPromptChange(showReferralPrompt)
  }, [showReferralPrompt, onReferralPromptChange])

  const getErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return t('auth.emailInUse')
      case 'auth/weak-password':
        return t('auth.weakPassword')
      case 'auth/invalid-email':
        return t('auth.invalidEmail')
      default:
        return t('auth.genericError')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email.trim())) {
      setError(t('auth.invalidEmail'))
      return
    }
    
    // Validate password length
    if (formData.password.length < 6) {
      setError(t('auth.weakPassword'))
      return
    }
    
    // Validate Instagram handle
    if (!formData.instagramHandle.trim()) {
      setError(t('auth.instagramRequired'))
      return
    }
    
    setLoading(true)
    setError(null)

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      )
      
      // Get and log device info on signup
      const deviceInfo = await getDeviceInfo()
      console.log('[SignupSlide] Signup - Device Info:', deviceInfo)
      logDeviceInfoDebug(deviceInfo)
      
      // Show referral prompt
      setNewUserId(userCredential.user.uid)
      setShowReferralPrompt(true)
      setFormData({ email: '', password: '', instagramHandle: '' })
      
      // Track sign up (non-blocking)
      try {
        await trackSignUp('email', 'onboarding', !!formData.instagramHandle.trim())
      } catch (trackError) {
        console.error('Analytics tracking failed (non-critical):', trackError)
      }
      
      // Track TikTok sign up event (non-blocking)
      try {
        await trackTikTokSignUp({
          email: formData.email,
          userId: userCredential.user.uid,
          content_type: 'email_signup',
          has_instagram: !!formData.instagramHandle.trim()
        })
      } catch (ttError) {
        console.error('TikTok pixel tracking failed (non-critical):', ttError)
      }
      
      // Track Meta Pixel sign up event (non-blocking)
      try {
        trackMetaPixelSignUp(
          formData.email,
          'email',
          !!formData.instagramHandle.trim()
        )
      } catch (metaError) {
        console.error('Meta Pixel tracking failed (non-critical):', metaError)
      }
      
      // Log successful signup (non-blocking)
      try {
        await logAuthAttempt('signup', formData.email, userCredential.user.uid)
      } catch (logError) {
        console.error('Security logging failed (non-critical):', logError)
      }
      
      // Save user data
      const additionalData = {
        createdAt: true,
        instagram: formData.instagramHandle.trim()
      }
      
      try {
        await handleUserData(userCredential.user, additionalData)
      } catch (userDataError) {
        console.error('User data saving failed (non-critical):', userDataError)
      }
    } catch (error: any) {
      setError(getErrorMessage(error.code))
    } finally {
      setLoading(false)
    }
  }

  const handleReferralSourceSubmit = async () => {
    if (!newUserId || !referralSource) return
    
    setLoading(true)
    
    try {
      // Save referral source to user document
      const userRef = doc(db, 'users', newUserId)
      const finalReferralSource = referralSource === 'Other' && referralSourceOther.trim() 
        ? referralSourceOther.trim() 
        : referralSource
      
      await updateDoc(userRef, {
        referralSource: finalReferralSource
      })
      
      // Complete onboarding
      onSuccess()
    } catch (error) {
      console.error('Error saving referral source:', error)
      // Still complete onboarding even if saving fails
      onSuccess()
    } finally {
      setLoading(false)
    }
  }

  // Show referral prompt after successful signup
  if (showReferralPrompt) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 py-6">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              All set! 🫶
            </h2>
            <p className="text-sm text-muted-foreground">
              Your account was created.
            </p>
          </div>

          <div className="space-y-3 pt-4">
            <label className="text-sm font-medium text-foreground text-center block mb-4">
              We would love to know how you heard about GetMoreTats?
            </label>
            <select
              value={referralSource}
              onChange={(e) => {
                setReferralSource(e.target.value)
                if (e.target.value !== 'Other') {
                  setReferralSourceOther('')
                }
              }}
              className="w-full bg-muted/50 rounded-md px-3 py-3 text-foreground border border-input focus:outline-none focus:ring-0"
            >
              <option value="">Select an option...</option>
              <option value="Instagram">Instagram</option>
              <option value="TikTok">TikTok</option>
              <option value="Friends">Friends</option>
              <option value="Tattoo Artist">Tattoo Artist</option>
              <option value="Google Search">Google Search</option>
              <option value="Other">Other</option>
            </select>
            
            {referralSource === 'Other' && (
              <Input
                type="text"
                value={referralSourceOther}
                onChange={(e) => setReferralSourceOther(e.target.value)}
                placeholder="Please specify..."
                className="w-full"
              />
            )}
          </div>

          <Button
            onClick={handleReferralSourceSubmit}
            disabled={!referralSource || loading}
            className="w-full h-12 text-base font-medium rounded-[var(--card-radius)]"
          >
            {loading ? t('common.loading') : 'Find your next tattoo!'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 py-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground">
            {t('auth.signup')}
          </h2>
        </div>

        <form id="signup-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Email Field */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('auth.email')}
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder={t('auth.emailPlaceholder')}
              required
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('auth.password')}
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Instagram Handle Field */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                {t('auth.instagramHandle')}
              </label>
              <button
                type="button"
                onClick={() => setShowInstagramInfo(true)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Why?
              </button>
            </div>
            <div className="relative">
              <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                value={formData.instagramHandle}
                onChange={(e) => handleInputChange('instagramHandle', e.target.value)}
                placeholder={t('auth.instagramPlaceholder')}
                className="pl-10"
                required
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)]">
              {error}
            </div>
          )}

          {/* Login Link */}
          <button
            type="button"
            onClick={onLogin}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2 mt-2"
          >
            Already have an account? Login
          </button>
        </form>
      </div>

      {/* Instagram Info Dialog */}
      <SignupFieldInfoDialog
        isOpen={showInstagramInfo}
        onOpenChange={setShowInstagramInfo}
        title="Instagram Handle"
        description="We use your Instagram to contact you if you win a Flash of the Week competition or if there are any issues related to booked appointments!"
      />
    </div>
  )
}
